import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { MessageCircle, Zap, LogIn, Building, Users, Shield, LogOut, Plus, ArrowRight, BarChart3, Workflow, CheckCircle, Send, MessageSquare, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/auth-context";
import { useWorkspaceOnboarding } from "@/hooks/use-workspace-onboarding";
import { useDefaultWorkspace } from "@/hooks/use-default-workspace";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { PricingPlans } from "@/components/PricingPlans";
import { HexIcon } from "@/components/ui/hex-icon";
import { FeatureCard } from "@/components/ui/feature-card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ContactService } from "@/services/contact-service";
import { useToast } from '@/hooks/use-toast';
import { LandingHeader } from '@/components/LandingHeader';
import { WorkspaceCreationModal } from "@/components/WorkspaceCreationModal";

const LandingPage = () => {
  const { user, loading, signOut } = useAuth();
  const { needsWorkspace, showModal, closeModal, reopenModal } = useWorkspaceOnboarding();
  const { defaultWorkspace, shouldRedirectToDefault } = useDefaultWorkspace();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    company: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = await ContactService.sendEnterpriseInquiry({
        name: contactForm.name,
        email: contactForm.email,
        company: contactForm.company,
        message: contactForm.message
      });

      if (result.success) {
        alert("Thank you for your beta access request! We'll review your application and get back to you soon.");
        setIsContactModalOpen(false);
        setContactForm({ name: "", email: "", company: "", message: "" });
      } else {
        throw new Error(result.error || "Failed to send message");
      }
    } catch (error) {
      console.error("Error sending contact form:", error);
      alert("There was an error sending your message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Auto-redirect notification */}
      {shouldRedirectToDefault && defaultWorkspace && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50">
          <Alert className="bg-primary text-primary-foreground border-primary/20 shadow-lg">
            <ArrowRight className="h-4 w-4" />
            <AlertDescription className="font-medium">
              Redirecting to your default workspace: {defaultWorkspace.name}...
            </AlertDescription>
          </Alert>
        </div>
      )}
      
      {/* Header */}
      <LandingHeader onContactModalOpen={() => setIsContactModalOpen(true)} />

      {/* Hero Section */}
      <div className="hex-hero">
        <div className="hex-hero-content">
          <h1 className="hex-title hex-gradient-text">
            Manufacturing Intelligence Platform
          </h1>
          <p className="hex-subtitle">
            Interactive quality control and data analysis for manufacturing excellence.
            <br />
            AI assisted collaboration to reveal hidden insights and embed problem resolution.
          </p>

          <div className="flex justify-center items-center mt-8 mb-8">
            <img 
              src="/bracket-image.png" 
              alt="Manufacturing Intelligence Platform" 
              className="max-w-md w-full h-auto rounded-lg shadow-lg"
            />
          </div>

          <div className="flex justify-center items-center mt-8">
            <button 
              className="hex-button-primary gap-2 text-base px-8 py-6 flex items-center"
              onClick={() => setIsContactModalOpen(true)}
            >
              <ArrowRight className="w-5 h-5" />
              Enquire
            </button>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div id="features" className="hex-container hex-section">
        <div className="text-center mb-12">
          <h2 className="hex-heading">Enterprise-Ready Manufacturing Platform</h2>
          <div className="hex-divider"></div>
        </div>

        <div className="hex-grid hex-grid-3 mb-16">
          <div className="hex-card hex-feature hex-hover-lift">
            <HexIcon icon={Workflow} className="mx-auto mb-4" />
            <h3 className="hex-subheading">Manufacturing Process Flow Modeler</h3>
            <p className="text-muted-foreground mb-4">
              Design and optimize your manufacturing workflow by mapping out process stations with interactive visual modeling.
            </p>
            <div className="bg-muted rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-blue-500 rounded"></div>
                  <span className="text-sm font-medium">Drive Unit CNC</span>
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Level 1</span>
                </div>
                <div className="flex gap-1">
                  <div className="w-3 h-3 bg-gray-300 rounded"></div>
                  <div className="w-3 h-3 bg-gray-300 rounded"></div>
                  <div className="w-3 h-3 bg-gray-300 rounded"></div>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Machining of Drive Unit Components</p>
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">View Station Data</span>
                  <div className="w-4 h-4 bg-gray-300 rounded"></div>
                </div>
              </div>
            </div>
            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-green-500 rounded"></div>
                  <span className="text-sm font-medium">End Bell CNC</span>
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Level 2</span>
                </div>
                <div className="flex gap-1">
                  <div className="w-3 h-3 bg-gray-300 rounded"></div>
                  <div className="w-3 h-3 bg-gray-300 rounded"></div>
                  <div className="w-3 h-3 bg-gray-300 rounded"></div>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Machining of End Bell Component</p>
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">View Station Data</span>
                  <div className="w-4 h-4 bg-gray-300 rounded"></div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="hex-card hex-feature hex-hover-lift">
            <HexIcon icon={MessageCircle} className="mx-auto mb-4" />
            <h3 className="hex-subheading">AI Assisted Collaboration</h3>
            <p className="text-muted-foreground mb-4">
              AI-powered chat and collaboration tools for manufacturing teams with intelligent data analysis and insights.
            </p>
            <div className="bg-muted rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">G</span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">GT27</span>
                    <span className="text-xs text-muted-foreground">10:04 AM</span>
                  </div>
                  <div className="bg-blue-100 rounded-lg p-2 mt-1">
                    <p className="text-xs">Torque check on Station 7 came in at 5.8 N-m—spec is 6.5±0.2 N-m. That's outside the lower limit.</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">AI</span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">WattFactory AI</span>
                    <span className="text-xs text-muted-foreground">10:05 AM</span>
                  </div>
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-2 mt-1">
                    <p className="text-xs">Analysis: This is a 12.3% deviation from target. Historical data shows similar issues correlate with worn tooling. Recommend checking tool wear on Station 7. Cp: 0.89, Cpk: 0.67 - process capability is below target.</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">G</span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Glen</span>
                    <span className="text-xs text-muted-foreground">10:06 AM</span>
                  </div>
                  <div className="bg-gray-100 rounded-lg p-2 mt-1">
                    <p className="text-xs">Thanks AI! I'll check the tool wear and recalibrate if needed.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="hex-card hex-feature hex-hover-lift">
            <HexIcon icon={BarChart3} className="mx-auto mb-4" />
            <h3 className="hex-subheading">Interactive Reports</h3>
            <p className="text-muted-foreground mb-4">
              Advanced 2D and 3D visualization with real-time data analysis and statistical process control.
            </p>
            
            {/* Interactive Reports Image */}
            <div className="bg-muted rounded-lg p-4 mb-4">
              <div className="mb-3">
                <div>
                  <h4 className="text-sm font-medium">Interactive Reports</h4>
                  <p className="text-xs text-muted-foreground">Advanced 2D and 3D visualization with real-time data analysis</p>
                </div>
              </div>
              
              {/* CNC Image */}
              <div className="bg-white rounded-lg p-3 mb-3">
                <img 
                  src="/cnc-image.png" 
                  alt="CNC Manufacturing Analysis" 
                  className="w-full h-auto rounded border"
                />
              </div>
              
              {/* Navigation buttons */}
              <div className="flex gap-2 mt-2 text-xs">
                <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">CNC Part</span>
                <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">10 markers</span>
                <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">10 samples</span>
              </div>
            </div>
            

            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium">SC08 - Diameter</span>
                <div className="w-4 h-4 bg-gray-300 rounded"></div>
              </div>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div className="bg-red-100 text-red-800 text-xs p-2 rounded text-center">
                  <div className="font-bold">Cp: 0.46</div>
                </div>
                <div className="bg-red-100 text-red-800 text-xs p-2 rounded text-center">
                  <div className="font-bold">Cpk: 0.41</div>
                </div>
              </div>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <span>Mean:</span>
                  <span className="font-medium">-0.003</span>
                </div>
                <div className="flex justify-between">
                  <span>StdDev:</span>
                  <span className="font-medium">0.018</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Workspace Examples */}
        <div className="hex-card max-w-4xl mx-auto text-center">
          <h3 className="hex-subheading mb-4">Your Dedicated Workspace</h3>
          <p className="text-muted-foreground mb-6">
            Each organization gets their own subdomain with complete data isolation
          </p>
          <div className="hex-grid hex-grid-1 gap-3 max-w-lg mx-auto">
            <div className="bg-muted rounded p-3">
              <span className="font-mono text-sm">evtol.wattfactory.tech</span>
            </div>
            <div className="bg-muted rounded p-3">
              <span className="font-mono text-sm">3dcnc.wattfactory.tech</span>
            </div>
            <div className="bg-muted rounded p-3">
              <span className="font-mono text-sm">evprototypes.wattfactory.tech</span>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Plans */}
      <div id="pricing">
        <PricingPlans />
      </div>

      {/* Contact Modal */}
      <Dialog open={isContactModalOpen} onOpenChange={setIsContactModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Request Beta Access</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleContactSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={contactForm.name}
                onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={contactForm.email}
                onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company">Company</Label>
              <Input
                id="company"
                value={contactForm.company}
                onChange={(e) => setContactForm({ ...contactForm, company: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                value={contactForm.message}
                onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                placeholder="Tell us about your manufacturing use case and why you'd like to join our beta program..."
                required
              />
            </div>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsContactModalOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting} className="flex-1">
                {isSubmitting ? "Sending..." : "Send Message"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="border-t bg-card/50 backdrop-blur-sm mt-16">
        <div className="hex-container py-8">
          <div className="text-center text-muted-foreground">
            <p>&copy; 2025 WattFactory. Advanced Manufacturing Intelligence Platform.</p>
          </div>
        </div>
      </footer>

      {/* Workspace Creation Modal */}
      {user && (
        <WorkspaceCreationModal
          isOpen={showModal}
          onClose={closeModal}
          userEmail={user.email}
          userName={user.display_name || user.email.split('@')[0]}
        />
      )}
    </div>
  );
};

export default LandingPage; 